import { type ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'primary' | 'soft' | 'ghost' | 'sealed';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={['btn', `btn--${variant}`, `btn--${size}`, className].filter(Boolean).join(' ')}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <span className="btn__spinner" aria-hidden />}
      <span className={loading ? 'btn__label--loading' : 'btn__label'}>{children}</span>
      <style>{btnCss}</style>
    </button>
  );
});

const btnCss = `
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: none; border-radius: var(--r-md);
  font-family: inherit; font-weight: 500; letter-spacing: 0.01em;
  cursor: pointer; -webkit-tap-highlight-color: transparent;
  transition:
    transform var(--d-quick) var(--ease-spring),
    box-shadow var(--d-base) var(--ease-soft),
    background var(--d-base) var(--ease-soft),
    opacity var(--d-base) var(--ease-soft);
  position: relative;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn:not(:disabled):hover { transform: translateY(-1px); }
.btn:not(:disabled):active { transform: translateY(0) scale(0.98); }

.btn--sm { padding: 8px 14px; font-size: var(--t-small); }
.btn--md { padding: 12px 18px; font-size: var(--t-body); }
.btn--lg { padding: 16px 24px; font-size: 1.05rem; }

.btn--primary {
  background: linear-gradient(135deg, var(--c-rose) 0%, var(--c-rose-deep) 100%);
  color: white;
  box-shadow: var(--shadow-sm), inset 0 1px 0 rgba(255,255,255,0.35);
}
.btn--primary:not(:disabled):hover { box-shadow: var(--shadow-md), inset 0 1px 0 rgba(255,255,255,0.35); }

.btn--soft {
  background: var(--c-blush); color: var(--c-ink);
  box-shadow: var(--shadow-xs);
}
.btn--soft:not(:disabled):hover { background: var(--c-rose); color: white; }

.btn--ghost {
  background: transparent; color: var(--c-ink);
}
.btn--ghost:not(:disabled):hover { background: rgba(196, 150, 164, 0.10); }

.btn--sealed {
  background: linear-gradient(135deg, var(--c-lavender) 0%, var(--c-lavender-mid) 100%);
  color: var(--c-ink);
  box-shadow: var(--shadow-sealed);
}

.btn__spinner {
  width: 14px; height: 14px;
  border: 2px solid currentColor; border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.btn__label--loading { opacity: 0.7; }
`;
